import axios, { InternalAxiosRequestConfig, AxiosError } from 'axios';
import { getToken } from './auth';

export interface Task {
  id: number;
  name: string;
  category: string;
  subcategory: string;
  startDate: string;
  endDate: string | null;
  isPaused?: string | null;
}

export interface CategorySubcategories {
  categories: string[];
  subcategories: { [key: string]: string[] };
  predefined_categories: string[];
  predefined_subcategories: { [key: string]: string[] };
}

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
});

// Interceptor: Automatically add JWT token to all requests
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Task operations
export const fetchTasks = async (): Promise<Task[]> => {
  const response = await api.get('/tasks');
  return response.data;
};

export type NewTask = Omit<Task, 'id'>;

export const addTask = async (task: NewTask): Promise<Task> => {
  const response = await api.post('/tasks', task);
  return response.data;
};

export const updateTask = async (task: Task): Promise<Task> => {
  const response = await api.put(`/tasks/${task.id}`, task);
  return response.data;
};

export const deleteTask = async (id: number): Promise<void> => {
  await api.delete(`/tasks/${id}`);
};

// Category operations
export const fetchCategories = async (): Promise<CategorySubcategories> => {
  const response = await api.get('/categories');
  return response.data;
};

export const addCategory = async (category: string): Promise<CategorySubcategories> => {
  const response = await api.post('/categories', { category });
  return response.data;
};

export const addSubcategory = async (category: string, subcategory: string): Promise<CategorySubcategories> => {
  const response = await api.post(`/categories/${category}/subcategories`, { subcategory });
  return response.data;
};

export const fetchSubcategories = async (category: string) => {
  const response = await api.get(`/categories/${category}/subcategories`);
  return response.data;
};

export const deleteCategory = async (category: string): Promise<CategorySubcategories> => {
  const response = await api.delete(`/categories/${category}`);
  return response.data;
};

export const deleteSubcategory = async (category: string, subcategory: string): Promise<CategorySubcategories> => {
  const response = await api.delete(`/categories/${category}/subcategories/${subcategory}`);
  return response.data;
};

export const renameCategory = async (oldName: string, newName: string): Promise<CategorySubcategories> => {
  const response = await api.put(`/categories/${oldName}`, { newName });
  return response.data;
};

export const renameSubcategory = async (category: string, oldName: string, newName: string): Promise<CategorySubcategories> => {
  const response = await api.put(`/categories/${category}/subcategories/${oldName}`, { newName });
  return response.data;
};
